/* eslint-disable import/prefer-default-export */
import { createDecimalRegExp } from 'utils/patterns';
import { aplicacionesCargosEnum } from 'views/Tramites/GPM/enums';

/**
 * Valida que el campo sea requerido
 * @param {string} value - Valor a validar
 * @throws {Error} Si el valor está vacío
 */
const validateRequired = (value) => {
  if (!value || value.toString().trim() === '') {
    throw new Error('El campo es requerido');
  }
};

/**
 * Valida el formato decimal del valor
 * @param {string} value - Valor a validar
 * @param {RegExp} regexp - Expresión regular para validar formato decimal
 * @throws {Error} Si el formato no es válido
 */
const validateDecimalFormat = (value, regexp) => {
  if (!regexp.test(value)) {
    throw new Error('Ingrese un valor decimal válido');
  }
};

/**
 * Valida que el valor parseado sea un número válido
 * @param {number} parsedValue - Valor parseado
 * @throws {Error} Si el valor no es un número válido
 */
const validateParsedValue = (parsedValue) => {
  if (!parsedValue || Number.isNaN(parsedValue)) {
    throw new Error('El campo es requerido');
  }
};

/**
 * Valida los límites de importe para tipo de gasto capturable
 * @param {number} parsedValue - Valor parseado
 * @param {Object} tipoDeGasto - Tipo de gasto con límites
 * @throws {Error} Si el valor excede los límites
 */
const validateImporteLimits = (parsedValue, tipoDeGasto) => {
  if (tipoDeGasto?.tipo_de_aplicacion === aplicacionesCargosEnum.CAPTURABLE) {
    const { importe_maximo, importe_minimo } = tipoDeGasto;

    if (importe_maximo && parsedValue > importe_maximo) {
      throw new Error('Ingrese un valor menor al importe máximo');
    }

    if (importe_minimo && parsedValue < importe_minimo) {
      throw new Error('Ingrese un valor mayor al importe mínimo');
    }
  }
};

/**
 * Obtiene el tipo de gasto seleccionado
 * @param {Object} formGasto - Instancia del formulario
 * @param {Array} tiposDeGastos - Lista de tipos de gastos
 * @returns {Object|null} Tipo de gasto encontrado o null
 */
const getSelectedTipoDeGasto = (formGasto, tiposDeGastos) => {
  const tipoDeGastoId = formGasto.getFieldValue('tipo_de_gasto');
  return tiposDeGastos.find((tipo) => tipo.id === tipoDeGastoId) || null;
};

/**
 * Determina la expresión regular a usar según el tipo de aplicación
 * @param {Object} tipoDeGasto - Tipo de gasto
 * @param {RegExp} decimal - Expresión regular para decimales con moneda
 * @param {RegExp} decimalDefault - Expresión regular para decimales por defecto
 * @returns {RegExp} Expresión regular apropiada
 */
const getDecimalRegexp = (tipoDeGasto, decimal, decimalDefault) => (
  tipoDeGasto?.tipo_de_aplicacion === aplicacionesCargosEnum.FORMULA
    ? decimalDefault
    : decimal
);

/**
 * Validador principal para el campo importe
 * @param {Object} rule - Regla de validación
 * @param {string} value - Valor a validar
 * @param {Object} context - Contexto con formGasto, tiposDeGastos, decimal, decimalDefault
 */
const validateImporte = async (rule, value, context) => {
  const {
    formGasto,
    tiposDeGastos,
    decimal,
    decimalDefault,
  } = context;

  // Validación de campo requerido
  validateRequired(value);

  // Obtener tipo de gasto seleccionado
  const tipoDeGasto = getSelectedTipoDeGasto(formGasto, tiposDeGastos);

  // Determinar expresión regular apropiada
  const regexp = getDecimalRegexp(tipoDeGasto, decimal, decimalDefault);

  // Validar formato decimal
  validateDecimalFormat(value, regexp);

  // Parsear y validar valor numérico
  const parsedValue = parseFloat(value);
  validateParsedValue(parsedValue);

  // Validar límites de importe si aplica
  validateImporteLimits(parsedValue, tipoDeGasto);
};

/**
 * Genera las reglas de validación para el formulario de gastos
 * @param {Object} moneda - Configuración de moneda
 * @param {Array} tiposDeGastos - Lista de tipos de gastos disponibles
 * @param {Object} formGasto - Instancia del formulario de gastos
 * @returns {Object} Objeto con las reglas de validación
 */
export const getFormRules = (moneda, tiposDeGastos, formGasto) => {
  const [decimal, maxLength] = createDecimalRegExp(moneda);
  const [decimalDefault] = createDecimalRegExp();

  return {
    required: [{ required: true }],
    importe: [
      {
        validator: async (rule, value) => {
          await validateImporte(rule, value, {
            formGasto,
            tiposDeGastos,
            decimal,
            decimalDefault,
          });
        },
      },
    ],
    maxLength,
  };
};
